import { useCallback } from 'react'
import { useSearchParams } from 'next/navigation'
import { GetProjectFiltersQuery } from '@/gql/generated/graphql'
import { FilterOption } from '@components/grid/filter-panel'

export enum ValidArtistsView {
  images = 'images',
  list = 'list'
}

type UseArtistsSortAndFilterReturn = {
  view: ValidArtistsView
  setView: (newView: ValidArtistsView) => void
  categoryFilter: FilterOption
  clearFilters: () => void
  numberOfActiveFilters: number
}

export const useArtistsSortAndFilter = (
  filterOptions?: GetProjectFiltersQuery,
  disableImageGrid = false
): UseArtistsSortAndFilterReturn => {
  const searchParams = useSearchParams()

  // Get a new searchParams string by merging the current
  // searchParams with a provided key/value pair
  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set(name, value)

      return params.toString()
    },
    [searchParams]
  )

  const addSearchParam = (name: string, value: string) => {
    window.history.pushState(null, '', `?${createQueryString(name, value)}`)
  }

  const view = disableImageGrid
    ? ValidArtistsView.list
    : ((searchParams.get('view') as ValidArtistsView) ?? ValidArtistsView.images)

  const setView = (newView: ValidArtistsView) => {
    addSearchParam('view', newView)
  }

  const setArrayValue = (newValue: string, existingValues: string[], name: string) => {
    let values
    if (existingValues.includes(newValue)) {
      const index = existingValues.indexOf(newValue)
      values = existingValues.slice(0, index).concat(existingValues.slice(index + 1))
    } else {
      values = [newValue, ...existingValues]
    }

    addSearchParam(name, JSON.stringify(values))
  }

  const categoryFilterVal: string[] = JSON.parse((searchParams.get('categoryFilter') as string) ?? '[]')

  const setCategoryFilter = (newCategoryFilterVal: string) => {
    setArrayValue(newCategoryFilterVal, categoryFilterVal, 'categoryFilter')
  }

  const categoryFilterOptions = [
    ...(filterOptions?.verticals.map((vertical) => ({
      label: vertical.project_vertical?.display_name ?? '',
      value: vertical.name
    })) ?? []),
    ...(filterOptions?.heritageTags.map((tag) => ({
      label: tag.display_name,
      value: tag.name
    })) ?? [])
  ]

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams.toString())
    const deleteKeys: string[] = []
    params.forEach((value, key) => {
      if (key !== 'view') deleteKeys.push(key)
    })
    deleteKeys.forEach((key) => params.delete(key))
    window.history.pushState(null, '', `?${params.toString()}`)
  }

  const numberOfActiveFilters = categoryFilterVal.length

  return {
    view,
    setView,
    categoryFilter: {
      name: 'Category',
      selectedOptions: categoryFilterVal,
      options: categoryFilterOptions,
      onChange: setCategoryFilter
    },
    clearFilters,
    numberOfActiveFilters
  }
}
